import { useCoreService } from 'core/services/useCoreService';
import { createContext, useEffect } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CardsTotalsByAccountData } from '../models/CardsTotalsByAccountData';
import { SearchCardsTotalsError } from '../models/errors/SearchCardsTotalsError';
import { ACCOUNT_TOTALS_PATH } from 'core/cuenta/routes';
import {
  SEARCH_BUSINESS_TOTALS_BY_PERIOD_PATH,
  SEARCH_BUSINESS_TOTALS_PATH,
} from 'core/empresa/routes';
import { COST_CENTRE_TOTALS_BY_ID_PATH } from 'core/centrocosto/routes';
import { isInvalidPeriod } from 'shared/validations/ValidatePeriodo';
import { useDataFetcher } from 'shared/http';
import { LoadingSpinner } from 'shared/spinner/components/LoadingSpinner';
import { useNotifications } from 'shared/notifications';

interface ICardsTotalsByAccountContext {
  cardsTotalsData: CardsTotalsByAccountData;
  searchParams: URLSearchParams;
  goToSearch: () => void;
  goToBusinessMovements: () => void;
}

export const CardsTotalsByAccountContext = createContext<ICardsTotalsByAccountContext>({
  cardsTotalsData: null,
  goToSearch: () => {},
  searchParams: null,
  goToBusinessMovements: () => {},
});

export const CardsTotalsByAccountProvider = ({ children }) => {
  const [cardsTotalsData, setCardsTotalsDataRequest] =
    useDataFetcher<CardsTotalsByAccountData>(null);
  const { accountNumber } = useParams();
  const [searchParams] = useSearchParams();
  const coreService = useCoreService();
  const { addNotification } = useNotifications();
  const navigate = useNavigate();
  const { state } = useLocation();

  useEffect(() => {
    let request = {
      numeroCuenta: accountNumber,
    };
    if (searchParams.has('mes') && searchParams.has('anio')) {
      request['mes'] = searchParams.get('mes');
      request['anio'] = searchParams.get('anio');
    }
    if ('mes' in request && 'anio' in request && isInvalidPeriod(request)) {
      addNotification('No puede ver movimientos más antiguos a un año', 'error');
      navigate(SEARCH_BUSINESS_TOTALS_BY_PERIOD_PATH);
    }
    setCardsTotalsDataRequest(() =>
      coreService.searchCardTotalsReport(request).then(
        (response) => {
          return response.body;
        },
        (err) => {
          const message = new SearchCardsTotalsError(err.error).message;
          addNotification(message, 'error');
          if (state && state['fromAccountTotals']) {
            navigate({
              pathname: ACCOUNT_TOTALS_PATH,
              search: searchParams.toString(),
            });
          } else {
            navigate(SEARCH_BUSINESS_TOTALS_PATH, {
              state: {
                accountNumber,
              },
            });
          }
          return null;
        },
      ),
    );
  }, [
    state,
    accountNumber,
    coreService,
    addNotification,
    navigate,
    searchParams,
    setCardsTotalsDataRequest,
  ]);

  const value = {
    cardsTotalsData: cardsTotalsData.data,
    searchParams,
    goToSearch: () => {
      const returnToSearch =
        searchParams.has('mes') && searchParams.has('anio')
          ? SEARCH_BUSINESS_TOTALS_BY_PERIOD_PATH
          : SEARCH_BUSINESS_TOTALS_PATH;
      if (state && state['fromAccountTotals']) {
        navigate({
          pathname: ACCOUNT_TOTALS_PATH,
          search: searchParams.toString(),
        });
      } else if (state && state['fromCostCentreTotals']) {
        navigate({
          pathname: COST_CENTRE_TOTALS_BY_ID_PATH.replace(
            ':ccId',
            state['fromCostCentreTotals'],
          ),
          search: searchParams.toString(),
        });
      } else {
        navigate(returnToSearch, {
          state: {
            accountNumber,
          },
        });
      }
    },
    goToBusinessMovements: () => {
      navigate(ACCOUNT_TOTALS_PATH, {
        state: {
          cuenta: accountNumber,
          fromAccountCardTotals: true,
        },
      });
    },
  };
  return (
    <CardsTotalsByAccountContext.Provider value={value}>
      <LoadingSpinner isLoading={cardsTotalsData.isLoading}>
        {cardsTotalsData.data && children}
      </LoadingSpinner>
    </CardsTotalsByAccountContext.Provider>
  );
};
